import { env } from "@/environment/environment";

interface DeploymentVersion {
  assetVersion?: string;
}

interface VersionReloadState {
  get: () => string | null;
  set: (assetVersion: string) => void;
  clear: () => void;
}

const RELOAD_VERSION_KEY = "asca.reloadVersion";

export const createVersionReloadState = (
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null,
  initialVersion: string | null,
): VersionReloadState => {
  let fallbackVersion = initialVersion;

  return {
    get: () => {
      try {
        return fallbackVersion || storage?.getItem(RELOAD_VERSION_KEY) || null;
      } catch {
        return fallbackVersion;
      }
    },
    set: assetVersion => {
      fallbackVersion = assetVersion;
      try {
        storage?.setItem(RELOAD_VERSION_KEY, assetVersion);
      } catch {
        // The in-memory fallback still protects this page from reload loops.
      }
    },
    clear: () => {
      fallbackVersion = null;
      try {
        storage?.removeItem(RELOAD_VERSION_KEY);
      } catch {
        // The in-memory state has already been cleared.
      }
    },
  };
};

export const getLatestAssetVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch("/version.json", { cache: "no-store" });
    if (!response.ok) return null;

    const deployment = await response.json() as DeploymentVersion;
    return deployment.assetVersion || null;
  } catch {
    return null;
  }
};

export const getVersionedUrl = (href: string, assetVersion: string): string => {
  const url = new URL(href);
  url.searchParams.set("appVersion", assetVersion);
  return url.toString();
};

export const createVersionCheck = (
  currentVersion: string,
  replaceLocation: (url: string) => void,
  getCurrentUrl: () => string,
  reloadState: VersionReloadState,
) => {
  let checkInProgress = false;
  let reloadRequested = false;

  return async () => {
    if (checkInProgress || reloadRequested) return;

    checkInProgress = true;
    const latestVersion = await getLatestAssetVersion();
    checkInProgress = false;

    if (!latestVersion) return;
    if (latestVersion === currentVersion) {
      reloadState.clear();
      return;
    }

    const currentUrl = getCurrentUrl();
    if (
      reloadState.get() === latestVersion
      || new URL(currentUrl).searchParams.get("appVersion") === latestVersion
    ) return;

    reloadRequested = true;
    reloadState.set(latestVersion);
    replaceLocation(getVersionedUrl(currentUrl, latestVersion));
  };
};

export const registerVersionCheckListeners = (
  checkForUpdate: () => Promise<void>,
  windowTarget: Pick<Window, "addEventListener">,
  documentTarget: Pick<Document, "addEventListener" | "visibilityState">,
) => {
  void checkForUpdate();
  windowTarget.addEventListener("pageshow", () => void checkForUpdate());
  documentTarget.addEventListener("visibilitychange", () => {
    if (documentTarget.visibilityState === "visible") void checkForUpdate();
  });
};

export const installVersionCheck = () => {
  const currentVersion = env.VUE_APP_ASSET_VERSION;
  if (!currentVersion) return;

  const initialVersion = new URL(window.location.href).searchParams.get("appVersion");
  let storage: Storage | null = null;
  try {
    storage = sessionStorage;
  } catch {
    // The URL marker seeds the in-memory fallback when storage is unavailable.
  }
  const reloadState = createVersionReloadState(storage, initialVersion);
  const checkForUpdate = createVersionCheck(
    currentVersion,
    url => window.location.replace(url),
    () => window.location.href,
    reloadState,
  );

  registerVersionCheckListeners(checkForUpdate, window, document);
};
